import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard, Package, ShoppingCart, ScanLine, ClipboardList, FileText, Users2,
  Percent, BarChart3, ScrollText, UserCog, Bell, Menu, LogOut, Boxes, Search, ShieldAlert
} from 'lucide-react';
import { dateTime } from '@/lib/format';
import { InstallButton } from '@/components/InstallButton';
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog';
import { KeyRound } from 'lucide-react';

const ADMIN_NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/purchases', label: 'Purchases', icon: ShoppingCart },
  { to: '/import', label: 'OCR Import', icon: ScanLine },
  { to: '/requests', label: 'Requests', icon: ClipboardList },
  { to: '/invoices', label: 'Invoices', icon: FileText },
  { to: '/customers', label: 'Customers', icon: Users2 },
  { to: '/commissions', label: 'Commissions', icon: Percent },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/users', label: 'Users', icon: UserCog },
  { to: '/admin-tools', label: 'Admin Tools', icon: ShieldAlert },
  { to: '/audit', label: 'Audit Log', icon: ScrollText },
];

const EMPLOYEE_NAV = [
  { to: '/scan', label: 'Scan & Request', icon: ScanLine, end: true },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/requests', label: 'My Requests', icon: ClipboardList },
  { to: '/invoices', label: 'My Invoices', icon: FileText },
  { to: '/commissions', label: 'My Commissions', icon: Percent },
];

function NavItems({ items, onNavigate }) {
  return (
    <nav className="space-y-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onNavigate}
          data-testid={`sidebar-nav-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}-link`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]' : 'text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground'
            }`
          }
        >
          <it.icon className="h-[18px] w-[18px]" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--primary))] text-white">
        <Boxes className="h-5 w-5" />
      </div>
      <div className="leading-tight">
        <div className="font-heading font-semibold">Lailany</div>
        <div className="text-[11px] text-muted-foreground">Inventory Manager</div>
      </div>
    </div>
  );
}

function NotificationsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get('/notifications'); setData(data); }
    catch (err) { console.error('Failed to load notifications:', err); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const markRead = async () => { await api.post('/notifications/read'); load(); };

  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (o) markRead(); }}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="relative" data-testid="notifications-open-button">
          <Bell className="h-[18px] w-[18px]" />
          {data.unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[hsl(var(--destructive))] px-1 text-[10px] font-semibold text-white">
              {data.unread}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent data-testid="notifications-panel">
        <SheetHeader><SheetTitle className="font-heading">Notifications</SheetTitle></SheetHeader>
        <ScrollArea className="h-[calc(100vh-100px)] mt-4 pr-3">
          {data.items.length === 0 && <p className="text-sm text-muted-foreground">No notifications yet.</p>}
          <div className="space-y-2">
            {data.items.map((n) => (
              <div key={n.id} className="rounded-lg border bg-white p-3" data-testid="notifications-item">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{n.title}</p>
                  <span className="text-[11px] text-muted-foreground">{dateTime(n.created_at)}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
              </div>
            ))}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export default function AppLayout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const items = isAdmin ? ADMIN_NAV : EMPLOYEE_NAV;

  return (
    <div className="app-bg min-h-dvh">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] flex-col border-r bg-white/80 backdrop-blur px-3 py-4">
        <Brand />
        <div className="mt-6 flex-1 overflow-y-auto">
          <NavItems items={items} />
        </div>
        <div className="border-t pt-3 text-[11px] text-muted-foreground px-2">Lailany Inc Warehouse</div>
      </aside>

      <div className="lg:pl-[260px]">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center gap-2 sm:gap-3 border-b bg-white/80 backdrop-blur header-safe pb-2 sm:pb-2.5 no-print">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] px-3">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="py-2"><Brand /></div>
              <div className="mt-4"><NavItems items={items} onNavigate={() => setMobileOpen(false)} /></div>
            </SheetContent>
          </Sheet>

          <div className="font-heading font-semibold lg:hidden">Lailany</div>
          <div className="ml-auto flex items-center gap-2">
            <InstallButton />
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="topbar-user-menu-button">
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-[hsl(var(--primary))] text-white text-xs">{(user?.name || 'U').slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                  <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="text-sm">{user?.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-menu-item"><KeyRound className="h-4 w-4 mr-2" /> Change password</DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1280px] mx-auto main-safe-bottom">
          {children}
        </main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
